import './as-help-hover.ts';
import { updateInputHelpHover } from './input-help-hover-utils.ts';
import type { SetValueOptions } from './types';
import { resolveSetValueSilence, upgradeProperty } from './utils';
import { AsInputBase, type InternalSetValueOptions } from './as-input-base.ts';
import styleText from './as-string-input.css?raw';

const DEFAULT_LABEL = 'String';

export class AsStringInput extends AsInputBase<'string'> {
  private static _lightStyleAppended = false;
  static get observedAttributes(): string[] {
    return ['value', 'label-default-text', 'label-dict-key', 'placeholder', 'disabled', 'readonly', 'locked', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'help-placement'];
  }

  private _inputEl: HTMLInputElement | null = null;
  private _labelRowEl: HTMLElement | null = null;
  private _labelTextEl: HTMLElement | null = null;
  private _helpHoverEl: HTMLElement | null = null;
  private _clearBtnEl: HTMLButtonElement | null = null;

  private _lastCommittedValue = '';
  private _isReflectingValueAttr = false;
  private _hasRendered = false;

  constructor() {
    super();
    this._handleInput = this._handleInput.bind(this);
    this._handleBlur = this._handleBlur.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._handleNativeChange = this._handleNativeChange.bind(this);
    this._handleClear = this._handleClear.bind(this);
  }

  // ============================================================================
  // Abstract method implementations (required by AsInputBase)
  // ============================================================================

  protected _getValue(): string {
    return this.value;
  }

  protected _applyValue(val: string, options: InternalSetValueOptions): void {
    this._setValueInternal(val, options);
  }

  protected _convertFromParamate(val: any): string {
    return String(val);
  }

  public override resetToDefault(): void {
    const attrValue = this._initialValueAttr ?? '';
    this.setValue(attrValue, { silenceEvents: true });
  }

  protected _getInputElement(): HTMLInputElement | null {
    return this._inputEl;
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  connectedCallback(): void {
    if (!this._hasRendered) {
      this._render();
      this._hasRendered = true;
    }

    upgradeProperty(this, 'value');

    if (this.hasAttribute('value')) {
      this._setValueInternal(this.getAttribute('value') ?? '', {
        emitInput: false,
        emitChange: false,
        reflectAttr: false,
        commit: true,
      });
    } else if (this._inputEl) {
      this._inputEl.value = this.value;
    }

    // Register with Paramate (from base class)
    this._registerParamate();

    this._updateLabel();
    this._updatePlaceholder();
    this._applyDisabledState();

    this._inputEl?.addEventListener('input', this._handleInput);
    this._inputEl?.addEventListener('blur', this._handleBlur);
    this._inputEl?.addEventListener('keydown', this._handleKeyDown);
    this._inputEl?.addEventListener('change', this._handleNativeChange);
    this._clearBtnEl?.addEventListener('click', this._handleClear);
  }

  disconnectedCallback(): void {
    this._inputEl?.removeEventListener('input', this._handleInput);
    this._inputEl?.removeEventListener('blur', this._handleBlur);
    this._inputEl?.removeEventListener('keydown', this._handleKeyDown);
    this._inputEl?.removeEventListener('change', this._handleNativeChange);
    this._clearBtnEl?.removeEventListener('click', this._handleClear);

    // Unregister from Paramate (from base class)
    this._unregisterParamate();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    switch (name) {
      case 'value':
        if (this._isReflectingValueAttr) {
          return;
        }
        this._setValueInternal(newValue ?? '', {
          emitInput: false,
          emitChange: true,
          reflectAttr: false,
          commit: true,
        });
        break;
      case 'label-default-text':
      case 'label-dict-key':
        this._updateLabel();
        break;
      case 'placeholder':
        this._updatePlaceholder();
        break;
      case 'disabled':
        this._applyDisabledState();
        break;
      case 'readonly':
      case 'locked':
        this._applyReadonlyState();
        break;
      case 'help-tooltip-default-text':
      case 'help-tooltip-dict-key':
      case 'help-placement':
        this._updateHelpHover();
        break;
    }
  }

  // ============================================================================
  // Public API
  // ============================================================================


  public setValue(val: string, opt?: SetValueOptions): void {
    const { silenceInput, silenceChange, silenceHistory } = resolveSetValueSilence(opt);
    if (silenceHistory) this._skipNextHistoryPush = true;
    this._setValueInternal(val ?? '', {
      emitInput: !silenceInput,
      emitChange: !silenceChange,
      reflectAttr: true,
      commit: true,
    });
    this._skipNextHistoryPush = false;
  }

  // ============================================================================
  // Private methods
  // ============================================================================

  private _render(): void {
    if (!AsStringInput._lightStyleAppended) {
      const styleEl = document.createElement('style');
      styleEl.textContent = styleText;
      styleEl.setAttribute('data-component', 'as-string-input');
      document.head.appendChild(styleEl);
      AsStringInput._lightStyleAppended = true;
    }

    // CRITICAL: Save hovercard BEFORE wiping innerHTML (which destroys all children)
    const savedHovercard = this.querySelector('as-hovercard');

    const hasLabel = this.hasAttribute('label-default-text') || this.hasAttribute('label-dict-key');

    this.innerHTML = /* html */ `
      <label class="col">
        <div class="as-string-input__label-row" style="display:${hasLabel ? 'flex' : 'none'};align-items:center;gap:4px;">
          <as-text class="label-text">${this.getAttribute('label-default-text') ?? ''}</as-text>
          <span class="help-hover-container"></span>
        </div>
        <div class="as-string-input-wrapper">
          <input type="text" />
          <button class="as-string-input-clear" type="button" aria-label="Clear" tabindex="-1">&#x2715;</button>
        </div>
      </label>
    `;

    this._labelRowEl = this.querySelector('.as-string-input__label-row');
    this._labelTextEl = this.querySelector('.label-text');
    this._helpHoverEl = this.querySelector('.help-hover-container');
    this._inputEl = this.querySelector('input');
    this._clearBtnEl = this.querySelector('.as-string-input-clear');

    // Pass saved hovercard since it was destroyed by innerHTML
    this._updateHelpHover(savedHovercard);
  }

  private _handleInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;

    this._setValueInternal(target.value, {
      emitInput: true,
      emitChange: false,
      reflectAttr: true,
      commit: false,
    });
  }

  private _handleBlur(): void {
    this._emitChangeIfNeeded();
  }

  private _handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this._emitChangeIfNeeded();
    }
  }

  private _handleNativeChange(): void {
    this._emitChangeIfNeeded();
  }

  private _handleClear(): void {
    if (this.disabled || this.readonly) return;
    this.setValue('');
    this._inputEl?.focus();
  }

  private _emitChangeIfNeeded(): void {
    if (!this._inputEl) {
      return;
    }
    const current = this._inputEl.value;
    if (current === this._lastCommittedValue) {
      return;
    }

    const previous = this._lastCommittedValue;
    this._emitChange(current, previous);
    this._lastCommittedValue = current;
  }

  private _setValueInternal(value: string, options: InternalSetValueOptions): void {
    const {
      emitInput = false,
      emitChange = false,
      reflectAttr = false,
      commit = false,
    } = options;
    const previous = this._value;
    const previousCommitted = this._lastCommittedValue;

    this._value = value;

    if (this._inputEl && this._inputEl.value !== value) {
      this._inputEl.value = value;
    }

    if (this._clearBtnEl) {
      this._clearBtnEl.classList.toggle('has-value', value.length > 0);
    }

    if (reflectAttr) {
      this._isReflectingValueAttr = true;
      try {
        if (value === '') {
          this.removeAttribute('value');
        } else {
          this.setAttribute('value', value);
        }
      } finally {
        this._isReflectingValueAttr = false;
      }
    }

    const shouldEmitInput = emitInput && previous !== value;
    const shouldEmitChange =
      emitChange && (previous !== value || previousCommitted !== value);

    if (shouldEmitInput) {
      this._emitInput(value, previous);
    }

    if (shouldEmitChange) {
      this._emitChange(value, previousCommitted);
    }

    if ((commit || shouldEmitChange) && this._lastCommittedValue !== value) {
      this._lastCommittedValue = value;
    }
  }

  private _emitInput(value: string, previous: string): void {
    const detail = {
      type: 'string' as const,
      value,
      previousValue: previous,
      phase: 'input' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-update', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private _emitChange(value: string, previous: string): void {
    const detail = {
      type: 'string' as const,
      value,
      previousValue: previous,
      phase: 'change' as const,
    };

    this.dispatchEvent(
      new CustomEvent('as-input-change', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
    this.dispatchEvent(new CustomEvent('change', { detail: { value } }));

    // Send to Paramate (from base class)
    this._sendToParamate(value);
  }

  private _updateLabel(): void {
    if (!this._labelTextEl) {
      return;
    }
    const dictKey = this.getAttribute('label-dict-key');
    const defaultText = this.getAttribute('label-default-text') ?? '';

    if (dictKey) {
      this._labelTextEl.setAttribute('dict-key', dictKey);
    } else {
      this._labelTextEl.removeAttribute('dict-key');
    }

    this._labelTextEl.textContent = defaultText;

    if (this._labelRowEl) {
      const hasLabel = !!defaultText || !!dictKey;
      this._labelRowEl.style.display = hasLabel ? 'flex' : 'none';
    }
  }

  private _updatePlaceholder(): void {
    if (!this._inputEl) {
      return;
    }
    const placeholder = this.getAttribute('placeholder') ?? '';
    this._inputEl.placeholder = placeholder;
  }

  protected override _applyDisabledState(): void {
    super._applyDisabledState();
    this._updateClearBtnState();
  }

  protected override _applyReadonlyState(): void {
    super._applyReadonlyState();
    this._updateClearBtnState();
  }

  private _updateClearBtnState(): void {
    if (this._clearBtnEl) {
      if (this.disabled || this.readonly) {
        this._clearBtnEl.setAttribute('disabled', '');
      } else {
        this._clearBtnEl.removeAttribute('disabled');
      }
    }
  }

  private _updateHelpHover(savedHovercard?: Element | null): void {
    updateInputHelpHover(this, this._helpHoverEl, savedHovercard);
  }
}

customElements.define('as-string-input', AsStringInput);
